import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  Flex,
  useToast,
  IconButton,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, AddIcon } from '@chakra-ui/icons';
import { InfluencerLocation, InfluencerLocationInput } from '@foodfluence/shared';
import LocationsListWithForm from './LocationsListWithForm';
import ShippingAddressForm from './ShippingAddressForm';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { influencerApi } from 'services/api/influencer';
import { Pill } from '../../../components/ui';
import { ff } from '../../../theme';

interface ShippingAddress {
  name: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface LocationsEditProps {
  locations: InfluencerLocation[];
  setLocations: (locations: InfluencerLocation[]) => void;
  lastSavedLocationsInfo: {
    locations: InfluencerLocation[];
    defaultShippingAddress: string | null;
  } | null;
  onLocationsSaved: (locations: InfluencerLocation[]) => void;
  onShippingAddressSaved: (address: string | null) => void;
  onBack: () => void;
}

const LocationsEdit: React.FC<LocationsEditProps> = ({
  locations,
  setLocations,
  lastSavedLocationsInfo,
  onLocationsSaved,
  onShippingAddressSaved,
  onBack
}) => {
  const toast = useToast();
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(null);
  const [defaultShippingAddressString, setDefaultShippingAddressString] = useState<string | null>(null);
  const [isShippingFormVisible, setIsShippingFormVisible] = useState(false);
  const [isEditingShipping, setIsEditingShipping] = useState(false);
  const { influencerData } = useContext(InfluencerDataContext);
  
  const [isLoadingShippingAddress, setIsLoadingShippingAddress] = useState(true);

  // Per-operation saving state flags
  const [isSavingLocations, setIsSavingLocations] = useState(false);
  const [isSavingShippingAddress, setIsSavingShippingAddress] = useState(false);

  // Helper function to parse the shipping address string
  const parseShippingAddress = (addressString: string): ShippingAddress | null => {
    try {
      // Format: "123 Main St, Apt 4, City, State 12345, Country"
      const parts = addressString.split(',').map(part => part.trim());
      
      if (parts.length < 3) return null;

      // Extract street (first part)
      const street = parts[0];
      
      // Check if there's a secondary address (Apt, Suite, etc.)
      let name = street;
      let cityIndex = 1;
      
      // If there's a secondary address, it will be in parts[1]
      // Otherwise, city is in parts[1]
      if (parts.length >= 4 && /^(Apt|Suite|Unit|Floor|Building)/.test(parts[1])) {
        name = `${street}, ${parts[1]}`;
        cityIndex = 2;
      }

      const city = parts[cityIndex];
      const stateZip = parts[cityIndex + 1].split(' ');
      const state = stateZip[0];
      const zipCode = stateZip[1] || '';
      const country = parts[cityIndex + 2] || '';

      return {
        name,
        street,
        city,
        state,
        zipCode,
        country,
      };
    } catch (error) {
      console.error('Error parsing address:', error);
      return null;
    }
  };

  /**
   * Autosave locations to backend
   * Similar to PersonalInformationEdit's autosaveField pattern
   */
  const autosaveLocations = async (updatedLocations: InfluencerLocation[]) => {
    if (!influencerData?.id || !lastSavedLocationsInfo) return;
    
    try {
      setIsSavingLocations(true);
      
      // Filter out invalid locations
      let finalLocations = updatedLocations;
      if (updatedLocations.length > 0 && updatedLocations[updatedLocations.length - 1].lat === 0 && updatedLocations[updatedLocations.length - 1].lng === 0) {
        finalLocations = updatedLocations.slice(0, -1);
      }

      // Convert to backend format
      const locationsToSend: (InfluencerLocationInput & { docId?: string })[] = finalLocations.map((loc, index) => ({
        city: loc.city,
        state: loc.state,
        country: loc.country,
        zipCode: loc.zipCode,
        lat: loc.lat,
        lng: loc.lng,
        maxCollabDistance: loc.maxCollabDistance,
        locationPriority: index + 1,
        ...(loc.docId && { docId: loc.docId }),
      }));

      await influencerApi.updateLocations({
        influencerId: influencerData.id,
        locations: locationsToSend,
      });
      
      // Update last saved value on success
      onLocationsSaved(updatedLocations);
    } catch (error) {
      console.error('Error autosaving locations:', error);
      // Revert to last saved
      setLocations(lastSavedLocationsInfo.locations);
      toast({
        title: "Couldn't save locations",
        description: "Reverted to last saved value",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingLocations(false);
    }
  };

  /**
   * Autosave shipping address to backend
   */
  const autosaveShippingAddress = async (addressString: string | null) => {
    if (!influencerData?.id || !lastSavedLocationsInfo) return;
    
    const hasAddressChanged = addressString !== lastSavedLocationsInfo.defaultShippingAddress;
    if (!hasAddressChanged) return;
    
    try {
      setIsSavingShippingAddress(true);
      
      await influencerApi.updateProfile({
        defaultShippingAddress: addressString || '',
      }, influencerData.id);
      
      // Update last saved value on success
      onShippingAddressSaved(addressString);
    } catch (error) {
      console.error('Error autosaving shipping address:', error);
      // Revert to last saved
      const lastSavedAddress = lastSavedLocationsInfo.defaultShippingAddress;
      setDefaultShippingAddressString(lastSavedAddress);
      if (lastSavedAddress) {
        const parsedAddress = parseShippingAddress(lastSavedAddress);
        if (parsedAddress) {
          setShippingAddress(parsedAddress);
        }
      } else {
        setShippingAddress(null);
      }
      toast({
        title: "Couldn't save shipping address",
        description: "Reverted to last saved value",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingShippingAddress(false);
    }
  };

  // Load initial shipping address from personalInfo subcollection via backend API
  useEffect(() => {
    if (!influencerData?.id) return;

    const fetchShippingAddress = async () => {
      try {
        setIsLoadingShippingAddress(true);
        const personalInfo = await influencerApi.getPersonalInfo(influencerData.id);
        if (personalInfo.defaultShippingAddress) {
          setDefaultShippingAddressString(personalInfo.defaultShippingAddress);
          const parsedAddress = parseShippingAddress(personalInfo.defaultShippingAddress);
          if (parsedAddress) {
            setShippingAddress(parsedAddress);
          }
        }
      } catch (error) {
        console.error('Error fetching shipping address:', error);
      } finally {
        setIsLoadingShippingAddress(false);
      }
    };

    fetchShippingAddress();
  }, [influencerData?.id]);

  // Shipping address handlers
  const handleAddShippingAddress = () => {
    setIsEditingShipping(false);
    setIsShippingFormVisible(true);
  };

  const handleEditShippingAddress = () => {
    setIsEditingShipping(true);
    setIsShippingFormVisible(true);
  };

  const handleDeleteShippingAddress = async () => {
    if (!influencerData?.id) return;

    setShippingAddress(null);
    setDefaultShippingAddressString(null);

    try {
      setIsSavingShippingAddress(true);
      await influencerApi.updateProfile({
        defaultShippingAddress: '',
      }, influencerData.id);
      onShippingAddressSaved(null);
    } catch (error) {
      console.error('Error deleting shipping address:', error);
      toast({
        title: "Couldn't delete shipping address",
        description: "Please try again",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingShippingAddress(false);
    }
  };

  const handleCancelShippingForm = () => {
    setIsShippingFormVisible(false);
    setIsEditingShipping(false);
  };

  const handleSaveShippingAddress = async (addressString: string) => {
    setDefaultShippingAddressString(addressString);
    
    // Parse and display the address
    const parsedAddress = parseShippingAddress(addressString);
    if (parsedAddress) {
      setShippingAddress(parsedAddress);
    }
    
    setIsShippingFormVisible(false);
    setIsEditingShipping(false);
    
    // Autosave the changes
    await autosaveShippingAddress(addressString);
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch" mb={6}>
        {/* ZIP CODES Section */}
        <Box>
          <Flex justify="space-between" align="center" mb={3}>
            <Flex align="center">
              <Text textStyle="eyebrow" color={ff.text2}>
                Your zip codes
              </Text>
              {isSavingLocations && (
                <Spinner size="sm" color={ff.muted} ml={2} />
              )}
            </Flex>
            <Text fontSize="12px" color={ff.text2}>
              Drag to reorder
            </Text>
          </Flex>

          <LocationsListWithForm
            locations={locations}
            setLocations={setLocations}
            onLocationsChange={autosaveLocations}
            enableDragAndDrop
            disabled={isSavingLocations}
          />
        </Box>

        {/* SHIPPING ADDRESSES Section */}
        <Box>
          <Flex align="center" mb={3}>
            <Text textStyle="eyebrow" color={ff.text2}>
              Shipping addresses
            </Text>
            {isSavingShippingAddress && (
              <Spinner size="sm" color={ff.muted} ml={2} />
            )}
          </Flex>

          {/* Show form if adding or editing */}
          {isLoadingShippingAddress ? (
            <Flex justify="center" py={4}>
              <Spinner size="sm" color={ff.muted} />
            </Flex>
          ) : isShippingFormVisible ? (
            <ShippingAddressForm
              onCancel={handleCancelShippingForm}
              onSave={handleSaveShippingAddress}
              shippingAddress={isEditingShipping ? shippingAddress : null}
              isEditing={isEditingShipping}
            />
          ) : shippingAddress ? (
            <Flex
              alignItems="flex-start"
              border="1px solid"
              borderColor={ff.line}
              borderRadius="20px"
              p={4}
              bg="white"
            >
              {/* Package Icon */}
              <Box mr={3} color={ff.ink} mt={1}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
              </Box>

              {/* Address Details */}
              <VStack align="start" spacing={0} flex="1">
                <HStack spacing={2} mb={1}>
                  <Text fontWeight={600} fontSize="15px" color={ff.ink}>
                    {shippingAddress.name}
                  </Text>
                  <Pill tone="grey">Default</Pill>
                </HStack>
                <Text fontSize="14px" color={ff.text2}>
                  {shippingAddress.street}
                </Text>
                <Text fontSize="14px" color={ff.text2}>
                  {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zipCode}
                </Text>
                <Text fontSize="14px" color={ff.text2}>
                  {shippingAddress.country}
                </Text>
              </VStack>

              {/* Action Buttons */}
              <HStack spacing={0} mt={-2} mr={-2}>
                <IconButton
                  icon={<EditIcon />}
                  w="44px"
                  h="44px"
                  minW="44px"
                  borderRadius="full"
                  aria-label="Edit Shipping Address"
                  variant="ghost"
                  color={ff.ink}
                  onClick={handleEditShippingAddress}
                  isDisabled={isSavingShippingAddress}
                />
                <IconButton
                  icon={<DeleteIcon />}
                  w="44px"
                  h="44px"
                  minW="44px"
                  borderRadius="full"
                  aria-label="Delete Shipping Address"
                  variant="ghost"
                  color={ff.danger}
                  onClick={handleDeleteShippingAddress}
                  isDisabled={isSavingShippingAddress}
                />
              </HStack>
            </Flex>
          ) : (
            <Button
              leftIcon={<AddIcon boxSize={3} />}
              variant="inkOutline"
              onClick={handleAddShippingAddress}
              h="48px"
              w="100%"
              borderStyle="dashed"
              isDisabled={isSavingShippingAddress}
            >
              Add shipping address
            </Button>
          )}
        </Box>

        {/* Disclaimer */}
        <Box bg={ff.grey} borderRadius="20px" p={4}>
          <Text fontSize="13px" color={ff.text2} lineHeight="tall">
            Creator locations help brands find you for local collaborations. Shipping addresses are used for product gifting campaigns.
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default LocationsEdit;